// km-api/client.js
// Client HTTP unico per /api/km. Riusa il token JWT già gestito dal login
// esistente (Specifica v1.1, §3: "il KM non introduce una seconda autenticazione").
//
// Regole applicate (Specifica v1.1, §4.3 e §7):
// - Bearer JWT sulle rotte protette; token pubblico del link SOLO su /sa/open
// - Nessun log di token, risposte questionario, PII o contenuti liberi
// - Timeout standard 30s, esteso a 300s per operazioni LLM documentate
// - Retry automatico SOLO su GET idempotenti, mai sulle mutation
// - Abort quando la vista viene abbandonata

import { KM_CONFIG } from './config.js';
import { mapResponseError, mapNetworkError } from './errorMapper.js';

/**
 * @param {() => string|null} getAuthToken - restituisce il JWT bearer corrente
 * @param {() => void} onUnauthorized - callback invocata su 401 (attiva flusso login esistente)
 */
export function createKmClient({ getAuthToken, onUnauthorized } = {}) {
  async function request(
    path,
    {
      method = 'GET',
      body,
      query,
      signal,
      isLlmOperation = false,
      isPublic = false,
      retry = method === 'GET',
      maxRetries = 2,
    } = {}
  ) {
    const url = new URL(KM_CONFIG.API_BASE_URL.replace(/\/$/, '') + path);
    if (query) {
      // Mai dati personali o sensibili in query string, salvo il token pubblico
      // del link previsto esplicitamente dal contratto (/sa/open?token=...).
      Object.entries(query).forEach(([k, v]) => {
        if (v !== undefined && v !== null) url.searchParams.set(k, v);
      });
    }

    const headers = { 'Content-Type': 'application/json' };
    if (!isPublic) {
      const token = getAuthToken?.();
      if (token) headers.Authorization = `Bearer ${token}`;
    }

    const timeoutMs = isLlmOperation ? KM_CONFIG.TIMEOUT_LLM_MS : KM_CONFIG.TIMEOUT_STANDARD_MS;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    // Propaga anche l'abort esterno (vista abbandonata dall'utente)
    if (signal) signal.addEventListener('abort', () => controller.abort(), { once: true });

    let attempt = 0;
    // eslint-disable-next-line no-constant-condition
    while (true) {
      try {
        const response = await fetch(url.toString(), {
          method,
          headers,
          body: body !== undefined ? JSON.stringify(body) : undefined,
          signal: controller.signal,
        });
        clearTimeout(timeoutId);

        if (response.status === 401 && !isPublic) {
          onUnauthorized?.();
          throw await mapResponseError(response);
        }
        if (!response.ok) {
          throw await mapResponseError(response);
        }
        if (response.status === 204) return null;
        return await response.json();
      } catch (err) {
        clearTimeout(timeoutId);
        if (err?.name === 'KmApiError') throw err;

        const timedOut = controller.signal.aborted && !signal?.aborted;
        const mapped = mapNetworkError(err, { timedOut });

        const isIdempotentRetryable =
          retry && method === 'GET' && (mapped.code === 'NETWORK' || mapped.code === 'TIMEOUT');
        if (isIdempotentRetryable && attempt < maxRetries) {
          attempt += 1;
          await new Promise((r) => setTimeout(r, 300 * 2 ** attempt)); // backoff
          continue;
        }
        throw mapped;
      }
    }
  }

  return {
    get: (path, opts) => request(path, { ...opts, method: 'GET' }),
    post: (path, body, opts) => request(path, { ...opts, method: 'POST', body, retry: false }),
    patch: (path, body, opts) => request(path, { ...opts, method: 'PATCH', body, retry: false }),
    // Aggiunti 24/09 — mancavano, servivano per l'editor questionari
    // (PUT /questionari/domande/{id}/opzioni sostituisce sempre l'elenco
    // intero, mai una modifica puntuale; DELETE su sezioni/domande in
    // bozza). Stesso schema esatto di patch, nessuna nuova logica.
    put: (path, body, opts) => request(path, { ...opts, method: 'PUT', body, retry: false }),
    delete: (path, opts) => request(path, { ...opts, method: 'DELETE', retry: false }),
  };
}
